/**
 * Her footfalls as the stealth system hears them: once per stride, never once
 * per tick. Beat 2 shipped per tick — sixty "footfalls" a second at a walk —
 * and the beat played as a tuning problem rather than as the bug it was
 * (`main.ts` beat 2 records the whole story). This is the one statement of the
 * rule for every room that listens; it was three copies until session 67.
 *
 * Simulation only (D-045): it reads her simulated gait and speed, never the
 * animator, which runs on the render step.
 */

import type { Gait } from '@/physics/character';
import type { GaitKind } from '@/audio/sounds/foley';

/**
 * Metres she covers between one audible footfall and the next.
 *
 * Her body is 0.55 m long and she takes short quick steps; 0.26 is a little
 * under half her length and matches what `animation.debug.cycles` produces at
 * a walk.
 */
export const ROSALIE_STRIDE = 0.26;

/**
 * What her gait sounds like to a listener. `still` makes no sound; `scramble`
 * is heard as a trot rather than as a sixth emission multiplier, which keeps
 * `foley.ts` the single table — and her claws on a kerb are the loudest thing
 * she does, at exactly the moment she is somewhere she should not be.
 */
export function heardGait(gait: Gait): GaitKind | null {
  if (gait === 'still') return null;
  return gait === 'scramble' ? 'trot' : gait;
}

export class FootfallStride {
  #metres = 0;

  /**
   * Accumulate one tick of travel. True on the tick a footfall lands.
   *
   * `canLand` is the room's extra condition (grounded; the listener present;
   * not carried) — distance still accumulates while it is false, so the step
   * lands the tick it becomes true. Standing still resets the stride: the
   * first step after a stop is a whole stride away, not a remainder.
   */
  step(distance: number, heard: GaitKind | null, canLand: boolean): boolean {
    this.#metres += distance;
    const lands = heard !== null && canLand && this.#metres >= ROSALIE_STRIDE;
    if (lands) this.#metres -= ROSALIE_STRIDE;
    if (heard === null) this.#metres = 0;
    return lands;
  }
}
